import {
  AuditOutlined,
  CalculatorOutlined,
  DashboardOutlined,
  FileTextOutlined,
  LogoutOutlined,
  RiseOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Grid, Layout, Menu, Tag, Typography } from 'antd';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import type { Role } from '../types';

const { Header, Sider, Content } = Layout;

interface NavItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  roles: Role[];
}

const NAV: NavItem[] = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: 'Investment Dashboard', roles: ['Admin', 'Accountant', 'Viewer'] },
  { key: '/investors', icon: <TeamOutlined />, label: 'Investors', roles: ['Admin', 'Accountant', 'Viewer'] },
  // Split the same way the inbound side is: a KPI dashboard and a searchable list, sitting right
  // next to their inbound counterparts. Access stays Admin-only, matching the API.
  { key: '/my-investments', icon: <RiseOutlined />, label: 'My Investment Dashboard', roles: ['Admin'] },
  { key: '/dealers', icon: <TeamOutlined />, label: 'Dealers', roles: ['Admin'] },
  { key: '/portal', icon: <WalletOutlined />, label: 'My Portfolio', roles: ['Investor'] },
  { key: '/reports', icon: <FileTextOutlined />, label: 'Reports', roles: ['Admin', 'Accountant', 'Viewer'] },
  { key: '/calculator', icon: <CalculatorOutlined />, label: 'Calculator', roles: ['Admin', 'Accountant', 'Viewer', 'Investor'] },
  { key: '/users', icon: <UserOutlined />, label: 'Users', roles: ['Admin'] },
  { key: '/audit-log', icon: <AuditOutlined />, label: 'Audit Log', roles: ['Admin'] },
  { key: '/dealer-portal', icon: <WalletOutlined />, label: 'My Investments', roles: ['Dealer'] },
];

const ROLE_COLOR: Record<Role, string> = {
  Admin: 'red',
  Accountant: 'blue',
  Viewer: 'default',
  Investor: 'green',
  Dealer: 'purple',
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);

  const isMobile = !screens.md;
  const items = NAV.filter((item) => user && item.roles.includes(user.role));

  // Highlights the parent entry when a detail route (/investors/12) is open.
  const selected = items
    .map((i) => i.key)
    .filter((key) => location.pathname === key || location.pathname.startsWith(`${key}/`));

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        breakpoint="md"
        collapsedWidth={isMobile ? 0 : 80}
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="dark"
      >
        <div
          style={{
            height: 56,
            margin: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 600,
            fontSize: collapsed ? 14 : 16,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {collapsed ? 'IMS' : 'Investment System'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selected}
          items={items.map(({ key, icon, label }) => ({ key, icon, label }))}
          onClick={({ key }) => {
            navigate(key);
            if (isMobile) setCollapsed(true);
          }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          {user && <Tag color={ROLE_COLOR[user.role]}>{user.role}</Tag>}
          <Dropdown
            menu={{
              items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Log out', danger: true }],
              onClick: () => {
                logout();
                navigate('/login', { replace: true });
              },
            }}
          >
            <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size="small" icon={<UserOutlined />} />
              {!isMobile && <Typography.Text strong>{user?.fullName}</Typography.Text>}
            </span>
          </Dropdown>
        </Header>

        <Content style={{ margin: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
